import type { AppTopic } from '@/backend/websocket'
import { createSocket } from '@/backend/websocket'
import { FeatureFlag } from '@/modules/App/featureFlags'
import { useFeatureFlags } from '@/modules/App/useFeatureFlags'
import { toast } from '@/shared/toast'
import { useAuth0 } from '@auth0/auth0-vue'
import { createSharedComposable, watchOnce } from '@vueuse/core'
import type { Channel, ChannelState } from 'phoenix'
import { ref, watch } from 'vue'

const wait = (attempts: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, [1000, 5000, 10000][attempts - 1] ?? 60000)
  })

type TopicEventHandlerFn = (...args: unknown[]) => void
type TopicSubscription = {
  channel: Channel
  refCount: number
  eventListeners: Map<string, Set<TopicEventHandlerFn>>
  state: ChannelState
}

export type EventAndHandler = {
  event: string
  handler: (payload: unknown) => void
}

/**
 * Function returned when adding an event listener to a topic. Returns
 * the event and handler that were added, so that they can be re-added
 * if subscribing to a new topic.
 */
export type CleanupFn = () => EventAndHandler

type PendingEventListener = {
  event: string
  handler: TopicEventHandlerFn
  cleanup: CleanupFn | null
}

export const useWebSocket = createSharedComposable(() => {
  const socket = ref<null | ReturnType<typeof createSocket>>(null)
  const connectionTries = ref(0)
  const isExperimentalBackendEnabled = useFeatureFlags(FeatureFlag.EXPERIMENTAL_BACKEND)

  const { isLoading, getAccessTokenSilently, isAuthenticated } = useAuth0()

  const socketState = ref('closed')

  const topicSubscriptions = new Map<AppTopic, TopicSubscription>()
  const pendingEventListeners = new Map<AppTopic, PendingEventListener[]>()
  const pendingSubscriptions = new Map<AppTopic, Promise<TopicSubscription>>()

  const updateSocketState = () => {
    socketState.value = socket.value?.connectionState() || 'closed'

    // notify the user of the current state with toasts
    notifyUser(socketState.value, connectionTries.value)

    // when the socket comes back online, reset the counter
    if (socketState.value === 'open' && connectionTries.value > 0) {
      connectionTries.value = 0
    }
  }

  /**
   * Connect to the backend websocket endpoint
   *
   * Should be called once per full page load, usually in the main application component.
   *
   * If the conditions of the connection change (such as the user logging in or out),
   * the socket should be disconnected and reconnected.
   */
  const connect = async (reconnect: boolean = false) => {
    if (reconnect && socket.value) {
      await new Promise<void>((resolve) => socket.value?.disconnect(resolve))
      socket.value = null
    }

    if (socket.value || isLoading.value || !isAuthenticated.value) {
      return
    }

    const token = await getAccessTokenSilently()
    socket.value = createSocket(token)
    socket.value?.onOpen(updateSocketState)
    socket.value?.onClose(updateSocketState)
    socket.value?.onError(async () => {
      await new Promise<void>((resolve) => socket.value?.disconnect(resolve))
      updateSocketState()
      socket.value = null

      connectionTries.value++
      if (connectionTries.value > 5) {
        toast.error('Failed to reconnect to the server. Please try again later.')
        return Promise.reject('Failed to reconnect to the server. Please try again later.')
      }

      await wait(connectionTries.value)
      // recursively try to reconnect
      connect()
    })
    socket.value?.connect()
  }

  /**
   * Creates a promise resolving channel using the app's websocket connection.
   * Will wait for the connection to be instantiated before resolving.
   *
   * Because it's only possible to call join on a phoenix channel once, there
   * cannot be a recycled pool of channels. Instead, the channel is created on
   * every call.
   *
   * It may potentially still be possible to hold a pool of current channels,
   * in order to reuse subscriptions to topics, but that's probably an
   * overoptimisation at this point.
   */
  const getChannel = (topic: AppTopic) =>
    new Promise<Channel>((resolve) => {
      if (socket.value) {
        resolve(socket.value.channel(topic))
      }

      watchOnce(socket, (socket) => {
        if (socket) {
          resolve(socket.channel(topic))
        }
      })
    })

  /** Joins a channel for the given topic and returns a subscription object */
  const createSubscription = async (topic: AppTopic): Promise<TopicSubscription> => {
    const channel = await getChannel(topic)
    const subscription: TopicSubscription = {
      channel,
      refCount: 1,
      eventListeners: new Map(),
      state: 'closed',
    }

    channel.onClose(() => {
      const sub = topicSubscriptions.get(topic)
      if (sub) {
        sub.state = 'closed'
      }
    })
    channel.onError(() => {
      const sub = topicSubscriptions.get(topic)
      if (sub) {
        sub.state = 'errored'
      }
    })

    // Join the channel
    try {
      await new Promise<void>((resolve, reject) => {
        const push = channel.join()
        push.receive('ok', () => {
          subscription.state = 'joined'
          resolve()
        })
        push.receive('error', (reason) => {
          subscription.state = 'errored'
          reject(reason)
        })
        push.receive('timeout', () => {
          subscription.state = 'errored'
          reject(new Error('Channel join timeout'))
        })
      })

      // Apply any pending event listeners now that the channel is joined
      const pending = pendingEventListeners.get(topic)

      if (pending) {
        pending.forEach((pendingListener) => {
          if (pendingListener.cleanup === null) {
            // Add to our tracking
            if (!subscription.eventListeners.has(pendingListener.event)) {
              subscription.eventListeners.set(pendingListener.event, new Set())
            }
            const listeners = subscription.eventListeners.get(pendingListener.event)
            if (listeners) {
              listeners.add(pendingListener.handler)
            }

            // Add to Phoenix channel
            /** Holds a number - numeric reference that Phoenix needs to remove the event listener */
            const subscriptionRef = subscription.channel.on(
              pendingListener.event,
              pendingListener.handler,
            )

            // Update the pending listener with its cleanup function
            pendingListener.cleanup = () => {
              subscription.channel.off(pendingListener.event, subscriptionRef)
              const eventListeners = subscription.eventListeners.get(pendingListener.event)
              if (eventListeners) {
                eventListeners.delete(pendingListener.handler)
              }

              return { event: pendingListener.event, handler: pendingListener.handler }
            }
          }
        })
        // Clear pending listeners for this topic
        pendingEventListeners.delete(topic)
      }

      return subscription
    } catch (error) {
      // Clean up failed subscription and any pending listeners
      pendingEventListeners.delete(topic)
      throw error
    }
  }

  /**
   * Subscribe to a topic with reference counting and event listener management.
   * Uses promise-based queueing to prevent race conditions when multiple concurrent
   * calls are made for the same topic.
   */
  const subscribeToTopic = async (topic: AppTopic): Promise<Channel> => {
    let subscription = topicSubscriptions.get(topic)

    if (subscription) {
      subscription.refCount++
      return subscription.channel
    }

    // Check if there's already a pending subscription for this topic
    let pendingPromise = pendingSubscriptions.get(topic)
    if (pendingPromise) {
      subscription = await pendingPromise
      subscription.refCount++
      return subscription.channel
    }

    pendingPromise = createSubscription(topic)
    pendingSubscriptions.set(topic, pendingPromise)

    try {
      subscription = await pendingPromise
      topicSubscriptions.set(topic, subscription)
      pendingSubscriptions.delete(topic)
      return subscription.channel
    } catch (error) {
      pendingSubscriptions.delete(topic)
      throw error
    }
  }

  /**
   * Unsubscribe from a topic, decreasing reference count.
   * If refCount reaches 0, the channel is closed and removed.
   */
  const unsubscribeFromTopic = async (topic: AppTopic): Promise<void> => {
    const subscription = topicSubscriptions.get(topic)
    if (!subscription) {
      return
    }

    subscription.refCount--

    if (subscription.refCount <= 0) {
      // Last subscriber - clean up the channel
      topicSubscriptions.delete(topic)
      try {
        await new Promise<void>((resolve) => {
          const push = subscription.channel.leave()
          push.receive('ok', () => resolve())
          push.receive('error', () => resolve())
          push.receive('timeout', () => resolve())
        })
      } catch {
        // eslint-disable-next-line no-console
        console.warn('Channel cleanup failed', topic)
      }
    }
  }

  /**
   * Add an event listener to a topic's channel.
   * Manages the listener lifecycle and cleanup.
   * If the topic isn't subscribed yet, the listener will be queued and applied once the channel is joined.
   */
  const addTopicEventListener = (
    topic: AppTopic,
    event: string,
    handler: (...args: unknown[]) => void,
  ): CleanupFn => {
    const subscription = topicSubscriptions.get(topic)

    if (!subscription) {
      // Queue the event listener for when the channel is joined
      if (!pendingEventListeners.has(topic)) {
        pendingEventListeners.set(topic, [])
      }

      const pendingListener: PendingEventListener = {
        event,
        handler,
        cleanup: null, // Will be set when the channel is joined
      }

      pendingEventListeners.get(topic)?.push(pendingListener)

      // Return cleanup function that works even for pending listeners
      return () => {
        if (pendingListener.cleanup) {
          // Listener was applied, use its cleanup
          pendingListener.cleanup()
        } else {
          // Listener is still pending, remove from pending queue
          const pending = pendingEventListeners.get(topic)
          if (pending) {
            const index = pending.indexOf(pendingListener)
            if (index > -1) {
              pending.splice(index, 1)
            }
            if (pending.length === 0) {
              pendingEventListeners.delete(topic)
            }
          }
        }

        return { event, handler }
      }
    }

    // Subscription exists, add listener immediately
    // Add to our tracking
    if (!subscription.eventListeners.has(event)) {
      subscription.eventListeners.set(event, new Set())
    }
    const listeners = subscription.eventListeners.get(event)
    if (listeners) {
      listeners.add(handler)
    }

    const subscriptionRef = subscription.channel.on(event, handler)

    // Return cleanup function
    return () => {
      subscription.channel.off(event, subscriptionRef)
      const eventListeners = subscription.eventListeners.get(event)
      if (eventListeners) {
        eventListeners.delete(handler)
      }

      return { event, handler }
    }
  }

  /**
   * Get the current state of a topic's channel
   */
  const getTopicState = (topic: AppTopic): ChannelState => {
    const subscription = topicSubscriptions.get(topic)
    return subscription?.state || 'closed'
  }

  /**
   * Get a managed channel for a topic (used by useWebSocketChannel for backward compatibility)
   */
  const getManagedChannel = async (topic: AppTopic): Promise<Channel> => {
    return subscribeToTopic(topic)
  }

  watch(isExperimentalBackendEnabled, () => {
    connect(true)
  })

  watch(isLoading, async () => {
    if (!isLoading.value) {
      await connect()
    }
  })

  return {
    connect,
    socketState,
    getChannel,
    getManagedChannel,
    subscribeToTopic,
    unsubscribeFromTopic,
    addTopicEventListener,
    getTopicState,
  }
})

export const notifyUser = (socketState: string, attempt: number) => {
  if (attempt >= 2 && socketState === 'open') {
    // Originally I wanted to close the warning toast, but
    // after dismissing a toast an error happens and all the next toasts are.. toast..
    // Until the issue is fixed we have a bit less nice experience here.
    // toast.dismiss()
    toast.success('Connection Restored! Thank you for your patience.')
  } else if (attempt > 1 && socketState === 'closed') {
    toast.warning(
      'Server or internet issue detected. Confirm your internet connection and please wait.',
      {
        //duration: Infinity,
      },
    )
  }
}
